import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container } from "@/components/ui/container";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 bg-gray-50">
        <Container className="py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy – The LabSwAPP</h1>
                <p className="text-gray-600">Effective Date: 2025-09-01</p>
              </div>

              <div className="prose prose-gray max-w-none space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  At The LabSwAPP, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and share information when you use our marketplace, where university labs can swap reagents. It applies to users in Europe (in line with the GDPR) and in the United States (in line with relevant state and federal privacy laws).
                </p>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
                  <p className="text-gray-700 mb-3">When you use The LabSwAPP, we may collect the following types of information:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Account Information:</strong> Name, email address, university or lab affiliation, and login details.</li>
                    <li><strong>Transaction Data:</strong> Records of swaps, offers, requests, and other marketplace activities.</li>
                    <li><strong>Usage Data:</strong> Log files, device information, and activity within the platform.</li>
                    <li><strong>Communication Preferences:</strong> Notification and messaging settings you control.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
                  <p className="text-gray-700 mb-3">We use your information to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Provide and operate the marketplace.</li>
                    <li>Facilitate transactions and maintain transaction records.</li>
                    <li>Communicate with you about your account and marketplace updates.</li>
                    <li>Improve and secure our services.</li>
                    <li>Comply with applicable legal obligations.</li>
                  </ul>
                  <p className="text-gray-700 mt-3 font-medium">We do not sell your personal data.</p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Legal Basis for Processing (Europe Only)</h2>
                  <p className="text-gray-700 mb-3">If you are located in the European Economic Area (EEA), we process your personal data on the following legal bases:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Performance of a Contract:</strong> To provide you access to the marketplace.</li>
                    <li><strong>Consent:</strong> Where required, such as for certain communications.</li>
                    <li><strong>Legitimate Interests:</strong> For improving services, ensuring security, and preventing misuse.</li>
                    <li><strong>Legal Obligation:</strong> To comply with applicable laws.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Storage and Retention</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Your data and transaction history are stored securely in accordance with applicable regulations.</li>
                    <li>We retain your data only as long as necessary to provide services, comply with legal obligations, or resolve disputes.</li>
                    <li>You may request deletion of your account at any time, and we will delete your data unless retention is required by law.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Your Rights</h2>
                  <p className="text-gray-700 mb-3">Depending on your location, you may have the following rights:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate information.</li>
                    <li><strong>Deletion:</strong> Request deletion of your account and personal data.</li>
                    <li><strong>Restriction/Objection:</strong> Limit or object to certain processing.</li>
                    <li><strong>Data Portability:</strong> Request transfer of your data to another service.</li>
                    <li><strong>Notification Preferences:</strong> Manage or opt out of communications at any time.</li>
                  </ul>
                  <p className="text-gray-700 mt-3">
                    To exercise these rights, contact us at{" "}
                    <a href="mailto:hello@labswapp.com" className="text-reagent-blue hover:underline">
                      hello@labswapp.com
                    </a>
                    .
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Transfers</h2>
                  <p className="text-gray-700">
                    If you access The LabSwAPP from outside the country where our servers are located, your data may be transferred internationally. For European users, we ensure appropriate safeguards (such as Standard Contractual Clauses) are in place.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Security</h2>
                  <p className="text-gray-700">
                    We use appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. However, no system is completely secure.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Children's Privacy</h2>
                  <p className="text-gray-700">
                    The LabSwAPP is intended for use by university labs and researchers. It is not directed to children under 16, and we do not knowingly collect data from children.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to This Policy</h2>
                  <p className="text-gray-700">
                    We may update this Privacy Policy from time to time. Any significant changes will be notified to you via the app or email.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact Us</h2>
                  <p className="text-gray-700 mb-3">
                    If you have any questions or concerns about this Privacy Policy or your data rights, please contact us at:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 font-medium">The LabSwAPP</p>
                    <p className="text-gray-700">
                      Email:{" "}
                      <a href="mailto:hello@labswapp.com" className="text-reagent-blue hover:underline">
                        hello@labswapp.com
                      </a>
                    </p>
                    <p className="text-gray-700">Address: Avenida Europa 49, Conil de la Frontera 11149, Spain</p>
                  </div>
                </section>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <Link
                  to="/"
                  className="inline-flex items-center text-reagent-blue hover:text-reagent-blue-dark font-medium"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;